import { Injectable, inject, signal } from '@angular/core';

import { LinxBranding } from '@shared/models';
import { darkenForContrastOn, darkenToLuminance, ensureReadableOn, hexToRgbTuple, hoverShade, readableTextColor } from '@shared/utils';

import { BRAND_CONFIG } from '../branding';

/** The white modal card every form and CTA is painted on. */
const CARD_SURFACE = '#ffffff';

/**
 * Contrast any brand-coloured FILL must clear against the app's light surfaces — the CTA button,
 * the stepper's active circle and its connector line. Deliberately stricter than the 1.5 floor used
 * for brand-as-text accents: at 1.5 a white brand still resolved to a pale #d2d2d2 that read as
 * disabled. 2 lands it at #b7b7b7 (a usable weight) while moving the default Cateno orange only
 * #ffa503 -> #fda403, which is imperceptible. WCAG's 3:1 bar for non-text UI components would be
 * the accessible choice but darkens that orange to a visibly different #ce8502, changing the
 * flagship brand on every existing workflow — a design decision, not a bug fix (KOR-14961 R1).
 *
 * linxapp's CustomerAppBrandingPreview carries the same number as BUTTON_MIN_CONTRAST; the two
 * must move together or the admin preview stops matching what customers see.
 */
const FILL_MIN_CONTRAST = 2;

/**
 * Applies per-BA branding (KOR-14793) to the running Linx customer app by overriding
 * the CSS custom properties defined in styles.scss `:root`. When a value is absent the
 * default RocketFNCL/Cateno theme remains in effect.
 *
 * Derived vars (`--btn-primary-color`, `--primary-contrast`, `--primary-accent`,
 * `--primary-on-dark`) are computed for readability (ported linxapp contrast logic) so a
 * dark or near-white custom brand never renders invisible text.
 *
 * `brandingReady` gates the shell's first paint so the orange default is never flashed
 * before the workflow's branding resolves.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  /** Host-based default brand (SALT on a salt host, Cateno otherwise). */
  private readonly brand = inject(BRAND_CONFIG);

  /** Vars driven directly by `primaryColor`; reset together so a no-branding workflow restores styles.scss defaults. */
  private readonly primaryProps = [
    '--primary',
    '--primary-color',
    '--btn-primary-bg',
    '--btn-primary-hover-bg',
    '--btn-link-hover-color',
    '--btn-primary-color',
    '--primary-contrast',
    '--primary-accent',
    '--primary-on-dark',
    '--btn-primary-border-color',
    '--primary-fill',
    '--primary-fill-contrast',
  ];

  /**
   * Vars that take the raw primary color verbatim. The button fill vars deliberately are NOT
   * here — they need a contrast-corrected value (see `applyBranding`).
   */
  private readonly rawPrimaryProps = ['--primary', '--primary-color', '--btn-link-hover-color'];

  /** Vars driven by `secondaryColor`; reset together for the same reason as `primaryProps`. */
  private readonly secondaryProps = [
    '--secondary',
    '--secondary-accent',
    '--btn-secondary-bg',
    '--btn-secondary-color',
    '--btn-secondary-hover-bg',
  ];

  /** Gates the shell's first paint; flips true once the final theme is known (any exit path). */
  readonly brandingReady = signal(false);

  /** Mark the theme resolved without applying branding (e.g. confirmation page reached without a fetch, or fetch error). */
  markReady(): void {
    this.brandingReady.set(true);
  }

  applyBranding(branding?: LinxBranding): void {
    const root = document.documentElement;

    // Always clear prior overrides first so navigating between workflows (or to one with
    // fewer branding fields) can't leak a previous BA's colours; removing the inline
    // property lets the styles.scss `:root` default win again.
    this.primaryProps.forEach((prop) => root.style.removeProperty(prop));
    root.style.removeProperty('--primary-rgb');
    this.secondaryProps.forEach((prop) => root.style.removeProperty(prop));

    if (branding?.primaryColor) {
      const primary = branding.primaryColor;
      this.rawPrimaryProps.forEach((prop) => root.style.setProperty(prop, primary));

      // The CTA fill is the one place the raw brand colour cannot be painted verbatim: a light
      // primary put a white button on the white card, delineated only by a grey hairline and so
      // reading as disabled (KOR-14961). Darken only as far as the contrast floor requires, so
      // every brand that already rendered correctly is left untouched.
      // One derived fill serves every brand-coloured fill on a light surface, so the stepper cannot
      // drift from the button (KOR-14961: the stepper was reported after the first fix, having gone
      // straight to the raw colour). Computed against the white card; the stepper's #f7f7f7 backdrop
      // makes the effective ratio marginally lower, still comfortably above the 1.5 accent floor.
      const fill = darkenForContrastOn(primary, CARD_SURFACE, FILL_MIN_CONTRAST);
      root.style.setProperty('--primary-fill', fill);
      root.style.setProperty('--primary-fill-contrast', readableTextColor(fill));
      root.style.setProperty('--btn-primary-bg', fill);
      root.style.setProperty('--btn-primary-hover-bg', hoverShade(fill));
      root.style.setProperty('--btn-primary-color', readableTextColor(fill)); // label on the fill actually painted
      // Always transparent: the fill now clears BUTTON_MIN_CONTRAST by construction, so there is
      // no near-invisible case left for a border to rescue. The variable is still set (rather than
      // left to the static default) so the reserved 1px in _cateno-overrides.scss never changes the
      // button height, and so this mirrors the admin preview's identical construct.
      root.style.setProperty('--btn-primary-border-color', 'transparent');

      // Contrast-aware derived colours (linxapp parity). `--primary-contrast` keeps deriving
      // from the RAW primary because it is paired with raw `var(--primary)` fills elsewhere
      // (e.g. the datepicker's selected day in _cateno-overrides.scss), not with the button fill.
      root.style.setProperty('--primary-contrast', readableTextColor(primary)); // text/icons on a primary fill
      root.style.setProperty('--primary-accent', ensureReadableOn(primary, CARD_SURFACE)); // brand text on white card
      root.style.setProperty('--primary-on-dark', ensureReadableOn(primary, '#111111', '#ffffff')); // brand text on black shell band

      const rgb = hexToRgbTuple(primary);
      if (rgb) {
        root.style.setProperty('--primary-rgb', rgb.join(', '));
      }
    }

    if (branding?.secondaryColor) {
      const secondary = branding.secondaryColor;
      root.style.setProperty('--secondary', secondary);
      root.style.setProperty('--secondary-accent', ensureReadableOn(secondary, CARD_SURFACE));

      // rocket-ui wires `--btn-secondary-bg` to `var(--secondary)` and pins the label to a
      // hardcoded #111111, so a dark secondary rendered an unreadable label and a light one an
      // invisible fill. Derive all three the same way the primary button does.
      const secondaryFill = darkenForContrastOn(secondary, CARD_SURFACE, FILL_MIN_CONTRAST);
      root.style.setProperty('--btn-secondary-bg', secondaryFill);
      root.style.setProperty('--btn-secondary-hover-bg', hoverShade(secondaryFill));
      root.style.setProperty('--btn-secondary-color', readableTextColor(secondaryFill));
    }

    // Page background echoes the brand: darken the primary to ~0.05 luminance so the
    // white card floats on top. When a workflow has no custom branding, fall back to the
    // host's default primary (SALT teal on a salt host, Cateno orange otherwise). Invalid
    // hex → #1a1a1a.
    const landingBackground = darkenToLuminance(branding?.primaryColor || this.brand.defaultPrimary);
    root.style.setProperty('--landing-bg', landingBackground);
    // KOR-15192: a phone browser paints its status and toolbar areas from theme-color; matching the page keeps the
    // form's background running into them instead of stopping at a black band
    root.ownerDocument.querySelector('meta[name="theme-color"]')?.setAttribute('content', landingBackground);

    this.brandingReady.set(true);
  }
}
